import z from "zod";

import {
  DASHBOARD_MCP_RESOURCE_SCOPES,
  MCP_READ_SCOPE,
  MCP_SKILLS_READ_SCOPE,
  MCP_SUPPORTED_SCOPES,
  SKILL_MESH_RESOURCE_SCOPES,
} from "./mcpScopes";

export const mcpResourceSchema = z.enum(["arti", "skill-mesh"]);
export type McpResource = z.infer<typeof mcpResourceSchema>;

export const MCP_RESOURCES = {
  arti: {
    defaultScope: MCP_READ_SCOPE,
    name: "ARTI MCP",
    path: "/public/mcp",
    scopes: DASHBOARD_MCP_RESOURCE_SCOPES,
  },
  "skill-mesh": {
    defaultScope: MCP_SKILLS_READ_SCOPE,
    name: "Skill Mesh MCP",
    path: "/public/skill-mesh/mcp",
    scopes: SKILL_MESH_RESOURCE_SCOPES,
  },
} as const;

export function scopesBelongToMcpResource(
  resource: McpResource,
  scopes: readonly string[],
): boolean {
  return scopes.every((scope) =>
    MCP_RESOURCES[resource].scopes.some((allowed) => allowed === scope),
  );
}

export const mcpResourceScopeSelectionSchema = z
  .object({
    resource: mcpResourceSchema.default("arti"),
    scopes: z
      .array(z.string().trim().pipe(z.enum(MCP_SUPPORTED_SCOPES)))
      .optional(),
  })
  .refine(
    ({ resource, scopes }) => scopesBelongToMcpResource(resource, scopes ?? []),
    {
      message: "Scopes must belong to the selected MCP resource only.",
      path: ["scopes"],
    },
  );

export function normalizeMcpResourceScopes(
  resource: McpResource,
  scopes?: readonly string[],
): string[] {
  const selection = mcpResourceScopeSelectionSchema.parse({ resource, scopes });
  const selected = new Set(
    selection.scopes?.length
      ? selection.scopes
      : [MCP_RESOURCES[resource].defaultScope],
  );
  return MCP_RESOURCES[resource].scopes.filter((scope) => selected.has(scope));
}

export function getMcpResourceForUrl(value: string): McpResource | null {
  const parsed = z.url().safeParse(value);
  if (!parsed.success) {
    return null;
  }
  const url = new URL(parsed.data);
  if (url.search || url.hash) {
    return null;
  }
  return (
    mcpResourceSchema.options.find(
      (resource) => MCP_RESOURCES[resource].path === url.pathname,
    ) ?? null
  );
}
